import { useCallback, useState } from 'react';

import {
    useTogglePromotionalBannerMutation,
    useToggleRestaurantBrandBannerMutation,
} from '@/services/innovorder/promotionalBanner/promotionalBanner.endpoints';
import type { PromotionalBanner } from '@/services/innovorder/promotionalBanner/promotionalBanner.type';
import type { PickupScheduleSlot } from '@/services/innovorder/restaurant/restaurant.type';

import { isInServiceHours } from '@/pages/PromotionalBanners/utils/isInServiceHours';

type UseBannerToggleParams = {
    token: string;
    restaurantId: number;
    schedules: PickupScheduleSlot[];
};

export const useBannerToggle = ({ token, restaurantId, schedules }: UseBannerToggleParams) => {
    const [toggleBanner] = useTogglePromotionalBannerMutation();
    const [toggleBrandBannerForRestaurant] = useToggleRestaurantBrandBannerMutation();
    const [confirmReloadCallback, setConfirmReloadCallback] = useState<(() => void) | null>(null);

    const executeToggle = useCallback(
        async (banner: PromotionalBanner) => {
            if (banner.brandId !== null) {
                await toggleBrandBannerForRestaurant({
                    token,
                    promotionalBannerId: banner.promotionalBannerId,
                    restaurantId,
                    isActive: !banner.isActive,
                });
            } else {
                await toggleBanner({
                    token,
                    promotionalBannerId: banner.promotionalBannerId,
                    isActive: !banner.isActive,
                });
            }
        },
        [token, restaurantId, toggleBanner, toggleBrandBannerForRestaurant],
    );

    const handleToggle = useCallback(
        (banner: PromotionalBanner) => {
            if (isInServiceHours(schedules)) {
                setConfirmReloadCallback(() => () => executeToggle(banner));
            } else {
                executeToggle(banner);
            }
        },
        [schedules, executeToggle],
    );

    const handleConfirmReload = useCallback(() => {
        if (confirmReloadCallback) {
            confirmReloadCallback();
        }
        setConfirmReloadCallback(null);
    }, [confirmReloadCallback]);

    const handleCancelReload = useCallback(() => {
        setConfirmReloadCallback(null);
    }, []);

    return {
        handleToggle,
        confirmReloadCallback,
        handleConfirmReload,
        handleCancelReload,
    };
};
